import { LockKeyhole, Smartphone, Unlock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePermissions } from '@/hooks/usePermissions';
import * as repo from '../data/votingRepository';
import { useVotingMutation } from '../hooks/useVoting';
import { VotingQr } from './VotingQr';

/** Election page tab: the QR voters scan, and the CIs locked after too many wrong PINs */
export function PhoneVotingPanel({ electionId, eventId }: { electionId: string; eventId: string | null }) {
  const { can } = usePermissions();
  const canUnlock = can('election.edit');
  const { data: locked = [] } = useQuery({
    queryKey: ['voting', 'locked', electionId],
    queryFn: () => repo.listLockedCis(electionId),
    enabled: canUnlock,
    refetchInterval: 10_000,
  });
  const unlock = useVotingMutation(electionId, (ci: string) => repo.unlockCi(electionId, ci), {
    success: 'CI desbloqueado',
    extraKeys: [['voting', 'locked', electionId]],
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[auto_1fr]">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Smartphone className="size-4" /> Votación desde el teléfono</CardTitle>
          <CardDescription>Este código no cambia: también va impreso en todas las credenciales.</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center"><VotingQr electionId={electionId} showLink /></CardContent>
      </Card>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cómo vota la gente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>1. Escanea el código de su credencial (o de la pantalla) con la cámara del teléfono, o escribe el enlace.</p>
            <p>2. Escribe su <strong>CI</strong> y el <strong>PIN de 6 dígitos</strong> de su credencial, o escanea el QR de su credencial.</p>
            <p>3. Elige a su candidato y confirma. Solo puede votar una vez por ronda.</p>
            <p>
              {eventId
                ? 'El PIN está impreso en la credencial (sticker). Si la perdió, la mesa de registro genera uno nuevo.'
                : 'El PIN de cada votante está en la pestaña Votantes.'}
            </p>
            <p>Tras 5 PIN incorrectos el CI se bloquea 15 minutos o hasta que lo desbloquee la mesa de registro o un administrador.</p>
          </CardContent>
        </Card>

        {canUnlock && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><LockKeyhole className="size-4" /> CI bloqueados ({locked.length})</CardTitle></CardHeader>
            <CardContent>
              {locked.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ninguno.</p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {locked.map((l) => (
                    <li key={l.ci_norm} className="flex items-center justify-between gap-3 p-2.5 text-sm">
                      <span><strong>{l.full_name}</strong> <span className="text-muted-foreground">· CI {l.ci_norm}</span></span>
                      <Button size="sm" variant="outline" disabled={unlock.isPending} onClick={() => unlock.mutate(l.ci_norm)}>
                        <Unlock className="mr-1.5 size-4" /> Desbloquear
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
